import { EscHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { UserRowRoleType } from '@modules/users/store/types';
import { getUserTypeFromUserRowRole } from '@modules/users/utils';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  GetUserByEmailDocument,
  useUpdateTenantDashboardSettingsMutation,
} from 'generated/graphql';
import { useGetUser } from 'v2/core/services/user';

interface ChangeDefaultRoleConfirmationModelContentProps {
  newRole: UserRowRoleType;
  onChange: () => void;
}

export const ChangeDefaultRoleConfirmationModalContent = ({
  newRole,
  onChange,
}: ChangeDefaultRoleConfirmationModelContentProps) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const { closeModal } = useMuiModalContext();

  const [updateTenantDashboardSettings, { loading: isUpdatingInProgress }] =
    useUpdateTenantDashboardSettingsMutation();

  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const onProceed = async () => {
    updateTenantDashboardSettings({
      variables: {
        id: tenantID,
        dashboardSettings: {
          defaultUserType: getUserTypeFromUserRowRole(newRole) ?? undefined,
        },
      },
      refetchQueries: [GetUserByEmailDocument],
      onError: () => {
        showErrorNotification({
          contentComponent: 'Failed to update the default role',
          props: {},
        });
      },
      onCompleted: () => {
        closeModal();
        showSuccessNotification({
          contentComponent: `Successfully updated the default role for new users to ${newRole}`,
          props: {},
        });
        onChange();
      },
    });
  };

  return (
    <>
      <ModalHeader title="Update default user role" showCloseButton={false} />
      <DialogContent dividers>
        <Typography variant="body1">
          All new users will be assigned the{' '}
          <Box component="strong">{newRole}</Box> role upon signing up, unless
          they’re invited with a different permission. Are you sure you want to
          proceed?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" m={2} spacing={2}>
          <Button
            variant="outlined"
            onClick={() => closeModal()}
            endIcon={<EscHotKeyIcon />}
          >
            Cancel
          </Button>
          <LoadingButton
            autoFocus
            variant="contained"
            onClick={onProceed}
            loading={isUpdatingInProgress}
          >
            Proceed
          </LoadingButton>
        </Stack>
      </DialogActions>
    </>
  );
};
